import * as React from 'react';
import * as ToastPrimitives from '@radix-ui/react-toast';
import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import { cn } from './utils';
import './toast.css';

export const ToastProvider = ToastPrimitives.Provider;

// Bottom-center pill viewport — snackbar pattern: the stack of toasts
// grows upward from the bottom, centered, narrow enough to read as a
// confirmation pill rather than a panel.
export const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      'fixed bottom-0 left-1/2 z-toast flex w-full max-w-md -translate-x-1/2 flex-col items-center gap-2 px-4 pb-6 outline-none',
      className
    )}
    {...props}
  />
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

// Pill grammar: `rounded-full` for a hugging shape (only when single
// line); when description is present the inner layout switches to
// `rounded-3xl` via `data-multiline`. Surface uses Pattern B
// (`bg-app-elevated` veneer + solid base set by `toast.css`).
//
// Variant text colors stay neutral (`text-app-text`); the leading icon
// carries the semantic hue (success/destructive). The pill itself is
// theme-neutral, the icon signals state.
const toastVariants = cva(
  'group pointer-events-auto relative flex w-fit max-w-full items-center gap-3 px-5 py-3 text-app-text shadow-glass backdrop-blur-2xl outline-none data-[multiline=true]:rounded-3xl data-[multiline=false]:rounded-full',
  {
    variants: {
      variant: {
        default: '',
        success: '',
        destructive: '',
      },
    },
    defaultVariants: { variant: 'default' },
  }
);

interface ToastProps
  extends React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root>,
    VariantProps<typeof toastVariants> {}

export const Toast = React.forwardRef<React.ElementRef<typeof ToastPrimitives.Root>, ToastProps>(
  ({ className, variant, ...props }, ref) => (
    <ToastPrimitives.Root
      ref={ref}
      data-toast-content
      className={cn(toastVariants({ variant }), className)}
      {...props}
    />
  )
);
Toast.displayName = ToastPrimitives.Root.displayName;

// Close button — kept for a11y but visually muted, surfaces only on
// hover/focus. Default toast flow is auto-dismiss; this is the escape
// hatch for keyboard users and long-lived destructive toasts.
export const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    toast-close=""
    className={cn(
      'ml-1 flex-none rounded-full p-1 text-muted-foreground opacity-0 transition-opacity hover:text-foreground focus:opacity-100 focus:outline-none group-hover:opacity-100',
      className
    )}
    {...props}
  >
    <X className="h-3.5 w-3.5" />
  </ToastPrimitives.Close>
));
ToastClose.displayName = ToastPrimitives.Close.displayName;

export const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    className={cn('text-sm font-medium leading-tight', className)}
    {...props}
  />
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;

export const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={cn('mt-0.5 text-xs text-muted-foreground leading-snug', className)}
    {...props}
  />
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

export type ToastActionElement = React.ReactElement;
